import { useEffect, useState } from "react";
import {
  PlainButton,
  Dialog,
  Button,
} from "@jugl-web/ui-components/cross-platform";
import { useTranslations } from "@jugl-web/utils";
import { useInvoicesItemFormPageContext } from "@web-src/modules/invoices/pages/InvoicesItemFormPage/context/useInvoicesItemFormPageContext";
import { TaxField } from "@web-src/modules/invoices/pages/InvoicesSettingsPage/pages/InvoicesBusinessInformationPage/components/InvoiceSettingsForm/components/TaxField";
import { v4 as uuid4 } from "uuid";
import { useInvoicesConfigList } from "@web-src/modules/invoices/pages/InvoicesSettingsPage/pages/InvoicesBusinessInformationPage/hooks/useInvoicesConfigList";
import { invoicesApi } from "@web-src/features/api/createApi";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { InvoiceTaxRow } from "@web-src/modules/invoices/types";
import { CreateInvoicesConfigPayload } from "@jugl-web/rest-api";
import { Tooltip } from "@jugl-web/ui-components";
import { ReactComponent as CardIcon } from "./assets/card.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as PenIcon } from "./assets/pen.svg";

export const InvoiceLevelTaxes = () => {
  const { t } = useTranslations();
  const {
    taxFields,
    onAddTaxField,
    onEditTaxField,
    onRemoveTaxField,
    onSetAsSelected,
    onRemoveFromSelected,
    selectedTaxes,
    formValues,
  } = useInvoicesItemFormPageContext();
  const { configId } = useInvoicesConfigList();
  const { entityId } = useEntitySelectedProvider();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [updateConfig] = invoicesApi.useUpdateConfigMutation();

  const [copied, setCopied] = useState<InvoiceTaxRow[]>(taxFields.invoice);
  const [copiedSelectedTaxes, setCopiedSelectedTaxes] = useState<string[]>([]);

  useEffect(() => {
    setCopied(taxFields.invoice);
  }, [taxFields.invoice]);

  useEffect(() => {
    setCopiedSelectedTaxes(selectedTaxes.invoice);
  }, [selectedTaxes.invoice]);

  const handleClose = () => {
    setIsDialogOpen(false);
    setTimeout(() => {
      setCopied(taxFields.invoice);
      setCopiedSelectedTaxes(selectedTaxes.invoice);
    }, 300);
  };

  const handleSave = () => {
    const taxesToRemove = taxFields.invoice.filter(
      (field) => !copied?.some((item) => item.id === field.id)
    );

    handleRemoveTaxesFromConfig(taxesToRemove.map((field) => field.id));

    taxesToRemove.forEach((field) => {
      onRemoveTaxField("invoice", field.id);
    });

    handleUpdateTaxesInConfig();

    copied?.forEach((field) => {
      const isSelected = copiedSelectedTaxes?.includes(field.id);

      if (isSelected) {
        onSetAsSelected("invoice", field.id);
      } else {
        onRemoveFromSelected("invoice", field.id);
      }
    });
    setIsDialogOpen(false);
  };

  const getCurrentTaxes = () => [
    ...taxFields.invoice.map((field) => ({
      id: field.id,
      name: field.name,
      type: "total",
      percentage: field.percentage,
      default: field.default,
    })),
    ...taxFields.item.map((field) => ({
      id: field.id,
      name: field.name,
      type: "item",
      percentage: field.percentage,
      default: field.default,
    })),
  ];

  const handleRemoveTaxesFromConfig = async (ids: string[]) => {
    const payload: Partial<CreateInvoicesConfigPayload> = {
      name: uuid4(),
      tax: getCurrentTaxes().filter((tax) => !ids.includes(tax.id)),
    };

    if (configId) {
      await updateConfig({
        entityId,
        configId,
        payload,
        logo: undefined,
      });
    }
  };

  const handleUpdateTaxesInConfig = async () => {
    try {
      const payload: Partial<CreateInvoicesConfigPayload> = {
        name: uuid4(),
        tax: [],
      };

      copied?.forEach((field) => {
        const isNew = !taxFields.invoice.some((item) => item.id === field.id);

        if (isNew) {
          payload.tax = [
            ...(payload.tax || []),
            {
              name: field.name,
              type: "total",
              percentage: field.percentage,
              default: field.default,
            },
          ];
          onAddTaxField("invoice", {
            ...field,
            selected: Boolean(copiedSelectedTaxes?.includes(field.id)),
          });
        } else {
          payload.tax = [
            ...(payload.tax || []),
            {
              name: field.name,
              type: "total",
              percentage: field.percentage,
              default: field.default,
            },
          ];

          onEditTaxField("invoice", field);
        }
      });

      if (configId) {
        await updateConfig({
          entityId,
          configId,
          payload,
          logo: undefined,
        });
      }
    } catch (error) {
      // pass
    }
  };

  return (
    <>
      <Tooltip
        delay="default"
        placement="right"
        renderTrigger={({ props, ref }) => (
          <PlainButton
            className="-my-3 flex h-10 w-10 items-center justify-center rounded-lg border border-solid border-grey-100"
            onClick={() => setIsDialogOpen(true)}
            ref={ref}
            {...props}
          >
            <PenIcon />
          </PlainButton>
        )}
      >
        {t({
          defaultMessage: "Edit",
          id: "common.edit",
        })}
      </Tooltip>
      <Dialog isOpen={isDialogOpen} onClose={handleClose} className="w-[720px]">
        <header className="rounded-t-3xl">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-2 font-secondary text-sm font-semibold text-dark">
              <CardIcon />
              {t({
                id: "invoices.create.invoice-discount-dialog-title",
                defaultMessage: "Tax Settings",
              })}
            </div>
            <PlainButton onClick={handleClose}>
              <CloseIcon />
            </PlainButton>
          </div>
          <div className="h-px w-full bg-dark-100" />
        </header>

        <div className="jugl__custom-scrollbar max-h-[calc(100vh-20rem)] space-y-12 overflow-y-auto px-8 py-12">
          <TaxField
            type="invoice"
            fields={copied}
            onAdd={(item) => {
              setCopied([...copied, item]);
            }}
            onEdit={(field) => {
              setCopied(
                copied.map((item) => (item.id === field.id ? field : item))
              );
            }}
            onRemove={(id) => {
              setCopied(copied.filter((item) => item.id !== id));
            }}
            onToggle={(id, isSelected) => {
              setCopiedSelectedTaxes(
                isSelected
                  ? [...copiedSelectedTaxes, id]
                  : copiedSelectedTaxes.filter((item) => item !== id)
              );
            }}
            selectedTaxes={copiedSelectedTaxes}
            max={9}
            currency={formValues.currency}
          />
        </div>
        <div className="mt-4 flex w-full items-center justify-between gap-4 p-12 pt-0">
          <Button
            className="w-full"
            color="grey"
            type="button"
            onClick={handleClose}
          >
            {t({
              id: "common.close",
              defaultMessage: "Close",
            })}
          </Button>
          <Button className="w-full" type="button" onClick={handleSave}>
            {t({
              id: "common.save",
              defaultMessage: "Save",
            })}
          </Button>
        </div>
      </Dialog>
    </>
  );
};
